import { useMemo, useState } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { WrapStyle } from '@/lib/object-effects/types/wrap-style'
import { isWrapSourceEligibleShape } from '@/lib/object-effects/text-wrap'
import { offsetControlsEnabledForStyle, sideEnabledForStyle } from '../helpers/wrap-style-sides'
import type { OffsetKey, WrapTarget } from '../types/wrap-offset'

/** The wrap-eligible selected objects, each one's current style, and which controls they enable. */
export function useWrapStyles(activeObjects: ShapeData[], interactionLocked: boolean) {
  /** Every style currently active across the selection — a single-object selection has at most one
   *  entry (identical to the old single `wrapStyle` state); a mixed multi-selection can have
   *  several, each independently highlighted (AC 8). Populated by the sync effect
   *  (`useWrapOffsetSync`), reading live manager data per target the same way the old
   *  single-target effect did. */
  const [styleByTargetId, setStyleByTargetId] = useState<Map<string, WrapStyle>>(new Map())

  const targets: WrapTarget[] = useMemo(
    () =>
      activeObjects
        .filter(isWrapSourceEligibleShape)
        .map((obj): WrapTarget => ({ id: obj.id, obj })),
    [activeObjects]
  )

  const controlsDisabled = interactionLocked || targets.length === 0
  const targetStyles = targets.map((t) => styleByTargetId.get(t.id) ?? 'none')
  const activeStyles = new Set(targetStyles)
  // Bounding Box: the shared preset/slider/input control edits all four independent sides at
  // once (a convenience shortcut). Wrap Shape: that same shared control is the ONLY active offset
  // editor (it reads/writes `wrapOffsetTop`, the uniform contour offset — see
  // `commonOffsetFromObj`) — it has no directional arrow icon, which fits "one generic offset
  // value" better than the "Top" field would; no directional box is individually editable for
  // this style. Jump: repurposed again to drive Top+Bottom together (its only two real fields —
  // AC 3.2). All four directional boxes stay disabled for Wrap Shape; only Left/Right stay
  // disabled for Jump — see `isSideOffsetDisabled` (AC 4.4). For multiple selected objects on
  // different styles, the control is enabled if ANY of them would use it (AC 7/8) — committing
  // still only ever writes the fields real for each individual target's own style.
  const isOffsetDisabled = controlsDisabled || !targetStyles.some(offsetControlsEnabledForStyle)

  /** Bounding Box makes all four directional boxes individually editable. Jump makes only
   *  Top/Bottom editable — Left/Right have no left/right text flow to offset (AC 3.2/4.4). Wrap
   *  Shape disables all four — the shared preset/slider/input control above is its ONLY active
   *  offset editor; every directional box just shows that one value as a display-only mirror
   *  (AC 4.4 — see `offsetStringsFromObj`). No Wrap disables all four too. Enabled if ANY selected
   *  target's own style would enable this side (AC 7/8) — a mixed Wrap Shape + Bounding Box
   *  selection enables Right/Bottom/Left too, since the Bounding Box target(s) do use them;
   *  committing only ever patches targets for which the side is actually real
   *  (`sideEnabledForStyle` gate in `commitOffset`/`applyLiveSideOffsetToAll`). */
  const isSideOffsetDisabled = (key: OffsetKey): boolean => {
    if (controlsDisabled) return true

    return !targetStyles.some((style) => sideEnabledForStyle(key, style))
  }

  return {
    targets,
    setStyleByTargetId,
    controlsDisabled,
    activeStyles,
    isOffsetDisabled,
    isSideOffsetDisabled,
  }
}
